import { assertNever } from '$lib/core/assert-never';
import { toRadians, vec, type Bounds, type Shape, type Vec2 } from '$lib/core/geom';
import type { Marquee } from '$lib/core/interaction';
import { LINE_TYPES, type NodeId, type Style } from '$lib/core/model';
import type { ResolvedEntity, ResolvedLayout } from '$lib/core/scene';
import type { SnapKind, SnapResult } from '$lib/core/snap';
import { gridSpec } from './grid';
import type { Palette } from './theme';
import { toScreen, type Viewport } from './viewport';

export interface RenderOptions {
  readonly viewport: Viewport;
  readonly palette: Palette;
  readonly entities: readonly ResolvedEntity[];
  /** Dimensions and views, drawn from their layouts. */
  readonly layouts: readonly ResolvedLayout[];
  readonly selection: ReadonlySet<NodeId>;
  readonly hoveredId: NodeId | null;
  /** Shapes drawn like the hovered one, such as those the code editor's cursor line drew. */
  readonly highlighted: ReadonlySet<NodeId>;
  readonly previews: readonly Shape[];
  /** Boxes outlined over the drawing, such as the one a picked comment is about. */
  readonly regions: readonly Bounds[];
  readonly snap: SnapResult | null;
  readonly marquee: Marquee | null;
  readonly showGrid: boolean;
  readonly gridSize: number;
}

export function render(ctx: CanvasRenderingContext2D, options: RenderOptions): void {
  const { viewport, palette } = options;

  ctx.save();
  ctx.fillStyle = palette.background;
  ctx.fillRect(0, 0, viewport.width, viewport.height);

  if (options.showGrid) drawGrid(ctx, options);
  drawAxes(ctx, options);

  for (const entity of options.entities) {
    if (!entity.visible || options.selection.has(entity.id)) continue;
    const hovered = entity.id === options.hoveredId || options.highlighted.has(entity.id);
    const width = widthOf(entity.style, viewport, 1.25);
    strokeShape(
      ctx,
      entity.shape,
      viewport,
      hovered ? palette.hover : (entity.style?.color ?? palette.entity),
      hovered ? Math.max(width, 2) : width,
      dashOf(entity.style),
    );
  }

  for (const entity of options.entities) {
    if (!entity.visible || !options.selection.has(entity.id)) continue;
    const width = Math.max(widthOf(entity.style, viewport, 1.25), 2.25);
    strokeShape(ctx, entity.shape, viewport, palette.selection, width, dashOf(entity.style));
  }

  for (const annotation of options.layouts) {
    if (!annotation.visible) continue;
    const selected = options.selection.has(annotation.id);
    const hovered = annotation.id === options.hoveredId || options.highlighted.has(annotation.id);
    const width = widthOf(annotation.style, viewport, 1);
    drawLayout(
      ctx,
      annotation,
      viewport,
      selected
        ? palette.selection
        : hovered
          ? palette.hover
          : (annotation.style?.color ?? palette.entity),
      selected ? Math.max(width, 2) : hovered ? Math.max(width, 1.75) : width,
    );
  }

  for (const preview of options.previews) {
    strokeShape(ctx, preview, viewport, palette.preview, 1.25, [5, 4]);
  }

  for (const region of options.regions) drawRegion(ctx, region, options);
  if (options.marquee) drawMarquee(ctx, options.marquee, options);
  if (options.snap) drawSnap(ctx, options.snap, options);

  ctx.restore();
}

function drawGrid(ctx: CanvasRenderingContext2D, options: RenderOptions): void {
  const { viewport, palette } = options;
  const spec = gridSpec(viewport, options.gridSize);
  const majorStep = spec.step * spec.majorEvery;

  for (const major of [false, true]) {
    ctx.beginPath();
    ctx.strokeStyle = major ? palette.gridMajor : palette.gridMinor;
    ctx.lineWidth = 1;

    for (let x = spec.min.x; x <= spec.max.x + 1e-9; x += spec.step) {
      if (isMajor(x, majorStep) !== major) continue;
      const screen = toScreen(viewport, vec(x, 0));
      ctx.moveTo(round(screen.x), 0);
      ctx.lineTo(round(screen.x), viewport.height);
    }

    for (let y = spec.min.y; y <= spec.max.y + 1e-9; y += spec.step) {
      if (isMajor(y, majorStep) !== major) continue;
      const screen = toScreen(viewport, vec(0, y));
      ctx.moveTo(0, round(screen.y));
      ctx.lineTo(viewport.width, round(screen.y));
    }

    ctx.stroke();
  }
}

function drawAxes(ctx: CanvasRenderingContext2D, options: RenderOptions): void {
  const { viewport, palette } = options;
  const origin = toScreen(viewport, vec(0, 0));

  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.strokeStyle = palette.axisX;
  ctx.moveTo(0, round(origin.y));
  ctx.lineTo(viewport.width, round(origin.y));
  ctx.stroke();

  ctx.beginPath();
  ctx.strokeStyle = palette.axisY;
  ctx.moveTo(round(origin.x), 0);
  ctx.lineTo(round(origin.x), viewport.height);
  ctx.stroke();
}

export function traceShape(ctx: CanvasRenderingContext2D, shape: Shape, viewport: Viewport): void {
  switch (shape.type) {
    case 'line': {
      const a = toScreen(viewport, shape.a);
      const b = toScreen(viewport, shape.b);
      ctx.moveTo(a.x, a.y);
      ctx.lineTo(b.x, b.y);
      return;
    }
    case 'circle': {
      const center = toScreen(viewport, shape.center);
      ctx.moveTo(center.x + shape.radius * viewport.zoom, center.y);
      ctx.arc(center.x, center.y, Math.abs(shape.radius) * viewport.zoom, 0, Math.PI * 2);
      return;
    }
    case 'arc': {
      const center = toScreen(viewport, shape.center);
      // Screen y grows downward, so a counter-clockwise world arc draws clockwise here.
      ctx.arc(
        center.x,
        center.y,
        Math.abs(shape.radius) * viewport.zoom,
        -toRadians(shape.start),
        -toRadians(shape.end),
        true,
      );
      return;
    }
    default:
      assertNever(shape);
  }
}

function strokeShape(
  ctx: CanvasRenderingContext2D,
  shape: Shape,
  viewport: Viewport,
  color: string,
  width: number,
  dash: readonly number[] = [],
): void {
  ctx.save();
  // The canvas copies the segments, so a shared pattern is never changed.
  ctx.setLineDash(dash as number[]);
  ctx.strokeStyle = color;
  ctx.lineWidth = width;
  ctx.beginPath();
  traceShape(ctx, shape, viewport);
  ctx.stroke();
  ctx.restore();
}

/** Text shorter than this many pixels is left out, since it cannot be read. */
const SMALLEST_TEXT = 3;

function drawLayout(
  ctx: CanvasRenderingContext2D,
  annotation: ResolvedLayout,
  viewport: Viewport,
  color: string,
  width: number,
): void {
  const { strokes, fills, text } = annotation.layout;
  ctx.save();
  ctx.setLineDash(dashOf(annotation.style) as number[]);
  ctx.strokeStyle = color;
  ctx.fillStyle = color;
  ctx.lineWidth = width;
  ctx.beginPath();
  for (const shape of strokes) traceShape(ctx, shape, viewport);
  ctx.stroke();
  for (const polygon of fills) {
    ctx.beginPath();
    polygon.forEach((point, index) => {
      const p = toScreen(viewport, point);
      if (index === 0) ctx.moveTo(p.x, p.y);
      else ctx.lineTo(p.x, p.y);
    });
    ctx.closePath();
    ctx.fill();
  }
  const size = text.height * viewport.zoom;
  if (size >= SMALLEST_TEXT) {
    const at = toScreen(viewport, text.at);
    ctx.translate(at.x, at.y);
    // Screen y grows downward, so a counter-clockwise world angle turns the other way here.
    ctx.rotate(-toRadians(text.angle));
    ctx.font = `${size}px sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(text.content, 0, 0);
  }
  ctx.restore();
}

/** A style's thickness in pixels, never under one; without one, the canvas's own width. */
const widthOf = (style: Style | undefined, viewport: Viewport, fallback: number): number =>
  style?.thick === undefined ? fallback : Math.max(style.thick * viewport.zoom, 1);

const dashOf = (style: Style | undefined): readonly number[] =>
  LINE_TYPES[style?.line ?? 'solid'].pattern;

function drawMarquee(
  ctx: CanvasRenderingContext2D,
  marquee: Marquee,
  options: RenderOptions,
): void {
  const { viewport, palette } = options;
  const a = toScreen(viewport, marquee.start);
  const b = toScreen(viewport, marquee.end);
  const rect = [
    Math.min(a.x, b.x),
    Math.min(a.y, b.y),
    Math.abs(b.x - a.x),
    Math.abs(b.y - a.y),
  ] as const;

  ctx.save();
  ctx.setLineDash(marquee.crossing ? [4, 3] : []);
  ctx.strokeStyle = palette.marquee;
  ctx.fillStyle = palette.marqueeFill;
  ctx.lineWidth = 1;
  ctx.fillRect(...rect);
  ctx.strokeRect(...rect);
  ctx.restore();
}

function drawRegion(ctx: CanvasRenderingContext2D, region: Bounds, options: RenderOptions): void {
  const { viewport, palette } = options;
  const a = toScreen(viewport, region.min);
  const b = toScreen(viewport, region.max);
  const rect = [a.x, b.y, b.x - a.x, a.y - b.y] as const;

  ctx.save();
  ctx.setLineDash([6, 4]);
  ctx.strokeStyle = palette.selection;
  ctx.fillStyle = palette.marqueeFill;
  ctx.lineWidth = 1.5;
  ctx.fillRect(...rect);
  ctx.strokeRect(...rect);
  ctx.restore();
}

function drawSnap(ctx: CanvasRenderingContext2D, snap: SnapResult, options: RenderOptions): void {
  const { viewport, palette } = options;
  const p = toScreen(viewport, snap.point);
  const r = 6;

  ctx.save();
  ctx.strokeStyle = palette.snap;
  ctx.fillStyle = palette.snap;
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  SNAP_GLYPHS[snap.kind](ctx, p, r);
  ctx.stroke();
  ctx.restore();
}

type Glyph = (ctx: CanvasRenderingContext2D, p: Vec2, r: number) => void;

const SNAP_GLYPHS: Readonly<Record<SnapKind, Glyph>> = {
  endpoint: (ctx, p, r) => ctx.rect(p.x - r, p.y - r, r * 2, r * 2),
  midpoint: (ctx, p, r) => {
    ctx.moveTo(p.x - r, p.y + r);
    ctx.lineTo(p.x + r, p.y + r);
    ctx.lineTo(p.x, p.y - r);
    ctx.closePath();
  },
  center: (ctx, p, r) => {
    ctx.moveTo(p.x + r, p.y);
    ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
  },
  quadrant: (ctx, p, r) => {
    ctx.moveTo(p.x, p.y - r);
    ctx.lineTo(p.x + r, p.y);
    ctx.lineTo(p.x, p.y + r);
    ctx.lineTo(p.x - r, p.y);
    ctx.closePath();
  },
  intersection: (ctx, p, r) => {
    ctx.moveTo(p.x - r, p.y - r);
    ctx.lineTo(p.x + r, p.y + r);
    ctx.moveTo(p.x + r, p.y - r);
    ctx.lineTo(p.x - r, p.y + r);
  },
  perpendicular: (ctx, p, r) => {
    ctx.moveTo(p.x - r, p.y - r);
    ctx.lineTo(p.x - r, p.y + r);
    ctx.lineTo(p.x + r, p.y + r);
    ctx.moveTo(p.x, p.y + r);
    ctx.lineTo(p.x, p.y);
    ctx.lineTo(p.x - r, p.y);
  },
  tangent: (ctx, p, r) => {
    ctx.moveTo(p.x + r, p.y + 2);
    ctx.arc(p.x, p.y + 2, r, 0, Math.PI * 2);
    ctx.moveTo(p.x - r, p.y - r);
    ctx.lineTo(p.x + r, p.y - r);
  },
  nearest: (ctx, p, r) => {
    ctx.moveTo(p.x - r, p.y - r);
    ctx.lineTo(p.x + r, p.y - r);
    ctx.lineTo(p.x - r, p.y + r);
    ctx.lineTo(p.x + r, p.y + r);
    ctx.closePath();
  },
  grid: (ctx, p, r) => {
    ctx.moveTo(p.x - r, p.y);
    ctx.lineTo(p.x + r, p.y);
    ctx.moveTo(p.x, p.y - r);
    ctx.lineTo(p.x, p.y + r);
  },
};

const isMajor = (value: number, majorStep: number): boolean =>
  Math.abs(value % majorStep) < 1e-9 || Math.abs(Math.abs(value % majorStep) - majorStep) < 1e-9;

const round = (value: number): number => Math.round(value) + 0.5;
